// Duplicate variable detection for compile-time imported `.luis` sources.
// It warns when imported sources redefine existing system variable names.
import type { LuisCompileWarning } from '../types'
import { extractSystemVariables } from '../variables/extract-system-variables'

// Build source variable registry and emit duplicate warnings.
export function registerSourceVariables(
  source: string,
  sourceId: string,
  owners: Map<string, string>,
  warnings: LuisCompileWarning[],
): void {
  const extracted = extractSystemVariables(source)
  const names = collectSystemVariableNames(extracted)

  // Check each variable name against previously seen owner map.
  for (const name of names) {
    const previousOwner = owners.get(name)
    // Check when variable is already declared in another source.
    if (previousOwner && previousOwner !== sourceId) {
      warnings.push({
        code: 'duplicate-import-variable',
        line: 1,
        message: `[${sourceId}] Variable "${name}" duplicates declaration from "${previousOwner}".`,
      })
    } else {
      // Keep warnings unchanged when declaration is unique.
    }

    // Check first owner to preserve warning provenance.
    if (!previousOwner) {
      owners.set(name, sourceId)
    } else {
      // Keep existing owner when variable already exists.
    }
  }
}

// Build all variable names from extracted registries.
function collectSystemVariableNames(extracted: ReturnType<typeof extractSystemVariables>): string[] {
  // Return merged list of all variable name keys.
  return [
    ...Object.keys(extracted.variables),
    ...Object.keys(extracted.classVariables),
    ...Object.keys(extracted.objectVariables),
    ...Object.keys(extracted.arrayVariables),
  ]
}

